import React, { useEffect, useState } from 'react';
import { toast, Loading } from '@cotera/client/app/components';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { useAuth } from '@workos-inc/authkit-react';
import { Button } from '../ui/button';
import { PROVIDER_ICONS } from './constants';
import {
  ConnectedStatusCard,
  InlineDeleteConfirmation,
} from './credential-connection-cards';
import { triggerLinearBotInstall } from './credential-strategy-views/linear-oauth-install-popout';
import { emitToolCredentialUpdated } from '@cotera/client/app/etc/emit-tool-credential-updated';
import { Assert } from '@cotera/utilities';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useLinearBotSettingsModel } from '@cotera/client/v0/settings/linear-bot-settings.model';
import { LinearBotTeamAgentsSection } from '@cotera/client/v0/settings/linear-bot-team-agents-section';

const LINEAR_PROVIDER = 'linear';

const LinearIcon =
  PROVIDER_ICONS[LINEAR_PROVIDER as keyof typeof PROVIDER_ICONS] ??
  (() => null);

export const LinearBotConnector: React.FC<{
  title?: string;
  description?: string;
  connectLabel?: string;
}> = ({
  title = 'Linear Agent',
  description = 'Connect Linear and install Coco as a workspace agent.',
  connectLabel = 'Install Coco',
}) => {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const { user } = useAuth();
  const model = useLinearBotSettingsModel();
  const status = useWatchableValue(model.status);
  const config = useWatchableValue(model.config);
  const [isInstalling, setIsInstalling] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const isInstalled = config.installed;
  const isLoading = status === 'loading';

  useEffect(() => {
    if (!isInstalling) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsInstalling(false);
    }, 5 * 60 * 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isInstalling]);

  const handleInstall = (): void => {
    const userId = user?.id;
    if (userId === undefined || userId.length === 0) {
      toast.error('Unable to determine user ID for Linear authorization');
      return;
    }

    setIsInstalling(true);
    setHasError(false);

    try {
      triggerLinearBotInstall({ orgId, userId }, () => {
        emitToolCredentialUpdated(orgId, LINEAR_PROVIDER);
        void model.refresh();
        setIsInstalling(false);
        toast.success('Linear agent installed successfully');
      });
    } catch {
      setIsInstalling(false);
      setHasError(true);
      toast.error('Failed to open Linear authorization popup');
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    setIsDisconnecting(true);
    try {
      const result = await client.toolsV2.disconnectLinearBotInstallation({});
      Assert.assertOk(result);
      emitToolCredentialUpdated(orgId, LINEAR_PROVIDER);
      await model.refresh();
      toast.success('Linear disconnected');
      setShowDisconnectConfirm(false);
    } catch {
      toast.error('Failed to disconnect Linear');
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <LinearIcon />
            </div>
            <div>
              <p className="text-sm font-medium">{title}</p>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          </div>

          {isLoading ? (
            <Loading.Shimmer className="h-8 w-24 rounded-md" />
          ) : isInstalled ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDisconnectConfirm((v) => !v)}
            >
              Disconnect
            </Button>
          ) : (
            <Button size="sm" onClick={handleInstall} disabled={isInstalling}>
              {isInstalling ? (
                <>
                  <Loading.Spinner variant="sm" />
                  Installing...
                </>
              ) : (
                connectLabel
              )}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="mt-4 space-y-4">
            <Loading.Shimmer className="h-24 w-full rounded-md" />
            <Loading.Shimmer className="h-32 w-full rounded-md" />
          </div>
        ) : isInstalled ? (
          <div className="mt-4 space-y-4">
            <ConnectedStatusCard
              title="Coco is installed in Linear"
              description={
                config.organizationId !== undefined
                  ? `Connected to organization ${config.organizationId}. Mention or delegate Coco on issues to start agent sessions.`
                  : 'Mention or delegate Coco on issues to start agent sessions.'
              }
              variant="success"
              isActive={true}
            />
            <LinearBotTeamAgentsSection />
          </div>
        ) : null}
      </div>

      {showDisconnectConfirm && isInstalled && (
        <InlineDeleteConfirmation
          isPending={isDisconnecting}
          onConfirm={() => {
            void handleDisconnect();
          }}
          onCancel={() => setShowDisconnectConfirm(false)}
        />
      )}

      {hasError && (
        <p className="text-sm text-destructive">
          Could not update Linear installation right now. Please try again.
        </p>
      )}
    </div>
  );
};
